import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Dialog, Popover } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { UserButton, SignedIn, SignedOut } from "@clerk/clerk-react";
import logo from "../../assets/images/svg/WPC LOGO.svg";
import { MdOutlineDashboard } from "react-icons/md";
import { useAuth } from "../../context/AuthContext";

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { isAdmin, pendingTokenCount } = useAuth();
  const CustomNavLink = ({ to, children }) => {
    const isActive = pathname === to;
    return (
      <NavLink
        to={to}
        className={`text-md font-Poppins tracking-widest font-semibold leading-6 ${
          isActive ? "text-myBlue" : "text-gray-900"
        }`}
      >
        {children}
      </NavLink>
    );
  };
  const handleWorkPermitClick = (e) => {
    e.preventDefault();
    const hasParams = search.length > 1;

    if (hasParams) {
      setShowConfirmModal(true);
    } else {
      navigate("/apply-now", { replace: true });
    }
  };

  // Confirm → Full reload with clean URL + Loader
  const confirmNavigation = () => {
    setShowConfirmModal(false);
    setIsNavigating(true); // ← Start loader

    // Full page reload with clean URL (this is what you wanted)
    setTimeout(() => {
      window.location.href = "/apply-now"; // Clean URL + Hard reload
    }, 300); // Small delay so loader is visible
  };

  const cancelNavigation = () => {
    setShowConfirmModal(false);
  };
  return (
    <header className="bg-white">
      <nav
        className="mx-auto flex md:max-w-4xl lg:max-w-[83rem] items-center justify-between px-6"
        aria-label="Global"
      >
        <div className="flex lg:flex-1">
          <NavLink to="/" className="">
            <img
              src={logo}
              alt="Work Permit Consultancy"
              className="w-48 md:w-52"
            />
          </NavLink>
        </div>

        <div className="flex lg:hidden">
          <button
            type="button"
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
            onClick={() => setMobileMenuOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <Popover.Group className="hidden lg:flex lg:gap-x-12 items-center">
          <CustomNavLink to="/home">Home</CustomNavLink>
          <CustomNavLink to="/about-us">About Us</CustomNavLink>
          <CustomNavLink to="/jobs">Jobs</CustomNavLink>

          {/* Beautiful Glowing Work Permit Button */}
          <button
            onClick={handleWorkPermitClick}
            className="relative group"
            disabled={isNavigating}
          >
            <div className="relative w-36 h-9 opacity-90 overflow-hidden rounded-full bg-gray-200 z-10">
              <div className="absolute flex items-center justify-center text-white z-[2] opacity-90 rounded-full inset-0.5">
                <span className="font-semibold text-myBlue text-base h-full opacity-100 w-full px-3 rounded-full bg-white flex items-center justify-center">
                  {isNavigating ? "Starting..." : "Apply Directly"}
                </span>
              </div>
              <div className="absolute inset-0 bg-gradient-to-r from-myBlue via-[#c06b02] to-myBlue opacity-100 -z-10 bg-[length:300%_100%] group-hover:animate-gradientFlow rounded-full" />
            </div>
          </button>
        </Popover.Group>

        <div className="hidden lg:flex lg:flex-1 lg:justify-end">
          <SignedIn>
            <div className="hidden lg:flex items-center gap-4">
              <NavLink
                to="/dashboard"
                className="text-gray-900 hover:text-myBlue relative"
                title="Dashboard"
              >
                <MdOutlineDashboard className="h-6 w-6" />
                {isAdmin && pendingTokenCount > 0 && (
                  <span className="absolute -top-[5px] -right-2 bg-red-600 text-white text-xs font-bold rounded-full h-4 w-4 flex items-center justify-center">
                    {pendingTokenCount}
                  </span>
                )}
              </NavLink>
              <UserButton afterSignOutUrl="/" />
            </div>
          </SignedIn>

          <SignedIn
            fallback={
              <NavLink
                to="/sign-in"
                className="text-sm font-Poppins font-semibold tracking-widest leading-6 border-2 border-myBlue hover:border-myBlue hover:bg-white hover:text-myBlue rounded-md px-5 py-2 bg-myBlue text-white"
              >
                Sign in
              </NavLink>
            }
          />
          <SignedOut>
            <NavLink
              to="/sign-in"
              className="text-sm font-Poppins font-semibold tracking-widest leading-6 border-2 border-myBlue hover:border-myBlue hover:bg-white hover:text-myBlue rounded-md px-5 py-2 bg-myBlue text-white"
            >
              Sign in
            </NavLink>
          </SignedOut>
        </div>
      </nav>

      {/* Mobile Menu */}
      <Dialog
        as="div"
        className="lg:hidden"
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
      >
        <div className="fixed inset-0 z-[99]" />
        <Dialog.Panel className="fixed inset-y-0 right-0 z-[99] w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="space-y-2 px-3 py-6">
            <NavLink
              to="/"
              className="-mx-3 block rounded-lg px-3 py-2 text-base font-Poppins font-semibold leading-7 text-gray-900 hover:bg-gray-50"
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </NavLink>
            <NavLink
              to="/about-us"
              className="-mx-3 block rounded-lg px-3 py-2 text-base font-Poppins font-semibold leading-7 text-gray-900 hover:bg-gray-50"
              onClick={() => setMobileMenuOpen(false)}
            >
              About Us
            </NavLink>
            <NavLink
              to="/jobs"
              className="-mx-3 block rounded-lg px-3 py-2 text-base font-Poppins font-semibold leading-7 text-gray-900 hover:bg-gray-50"
              onClick={() => setMobileMenuOpen(false)}
            >
              Jobs
            </NavLink>

            <button
              onClick={handleWorkPermitClick}
              className="w-full -mx-3 block rounded-lg px-3 py-2 text-base font-Poppins font-semibold leading-7 text-left hover:bg-gray-50"
              disabled={isNavigating}
            >
              {isNavigating ? "Starting..." : "Apply Directly"}
            </button>

            <NavLink
              to="/dashboard"
              className="-mx-3 block rounded-lg px-3 py-2 text-base font-Poppins font-semibold leading-7 text-gray-900 hover:bg-gray-50"
              onClick={() => setMobileMenuOpen(false)}
            >
              Dashboard
            </NavLink>
          </div>
        </Dialog.Panel>
      </Dialog>

      {/* Your Desired Beautiful Confirmation Modal */}
      <Dialog
        as="div"
        className="relative z-[100]"
        open={showConfirmModal}
        onClose={cancelNavigation}
      >
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm"
          aria-hidden="true"
        />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-md w-full bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="bg-myBlue px-6 py-4">
              <Dialog.Title className="text-xl font-bold text-white flex items-center gap-2">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                Start Work Permit Application
              </Dialog.Title>
            </div>

            <div className="px-6 py-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-myGolden"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-gray-800 font-medium mb-2">
                    Are you sure you want to start a{" "}
                    <strong>Work Permit Application</strong>?
                  </p>
                  <p className="text-gray-500 text-sm">
                    This will pause the current application and start a new Work
                    Permit application for your selected destination
                  </p>
                </div>
              </div>{" "}
            </div>

            <div className="bg-gray-50 px-6 py-4 flex gap-3 justify-end">
              <button
                onClick={cancelNavigation}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={isNavigating}
              >
                Cancel
              </button>
              <button
                onClick={confirmNavigation}
                disabled={isNavigating}
                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-myBlue to-myGolden rounded-lg hover:from-myBlue hover:to-myGolden disabled:opacity-70 flex items-center gap-2"
              >
                {isNavigating && (
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                )}
                Yes, Start Application
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </header>
  );
}
